'use client';

import Script from 'next/script';

import '@styles/styles.scss';
import '@styles/app/local.scss';
import { usePathname } from 'next/navigation';
import { ThemeFonts, RootProviders } from '@theme';
import { CustomHead } from '@layout';

type RootLayoutProps = Readonly<{
  children: React.ReactNode;
}>;

export default function RootLayout({ children }: RootLayoutProps) {
  const pathname = usePathname();
  const gaMeasurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

  return (
    <html
      lang={pathname.startsWith('/es') ? 'es' : 'en'}
      suppressHydrationWarning
      className={ThemeFonts.variable}
    >
      <head>
        <CustomHead />
        {gaMeasurementId ? (
          <>
            <Script
              src={`https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`}
              strategy='lazyOnload'
            />
            <Script id='google-analytics' strategy='lazyOnload'>
              {`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${gaMeasurementId}');
              `}
            </Script>
          </>
        ) : null}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              try {
                const params = new URLSearchParams(window.location.search);
                const qRaw = params.get("data-theme") ?? params.get("theme");
                const q = qRaw ? String(qRaw).trim().toLowerCase() : "";
                const fromQuery = q === "light" || q === "dark" ? q : null;

                const raw = localStorage.getItem("theme");
                const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
                let effectiveTheme = systemTheme;

                if (raw === "light" || raw === "dark") {
                  effectiveTheme = raw;
                } else if (raw === "system") {
                  effectiveTheme = systemTheme;
                  try {
                    localStorage.setItem("theme", effectiveTheme);
                  } catch (e2) {}
                } else if (fromQuery) {
                  effectiveTheme = fromQuery;
                  try {
                    localStorage.setItem("theme", fromQuery);
                  } catch (e3) {}
                }
                document.documentElement.setAttribute("data-theme", effectiveTheme);
              } catch (e) {}
            `,
          }}
        />
      </head>
      <body suppressHydrationWarning>
        <RootProviders>{children}</RootProviders>
      </body>
    </html>
  );
}
